import { useMemo, useState, useCallback, useEffect } from 'react'
import { AdminTable, type Column } from './AdminTable'
import { Pagination } from './Pagination'
import { DeleteButton } from './DeleteButton'
import { LoadingBar } from './LoadingBar'
import { useAdminTab } from '@/hooks/useAdminTab'
import { fetchAdminWords, deleteAdminWord } from '@/api/admin'
import type { AdminWordSummary } from '@/types/admin'

interface WordsTabProps {
  active: boolean
}

export function WordsTab({ active }: WordsTabProps) {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  const fetcher = useCallback(
    (page: number) => {
      const params = debouncedSearch ? { q: debouncedSearch } : undefined
      return fetchAdminWords(page, params)
    },
    [debouncedSearch],
  )

  const { data, meta, loading, setPage, refresh } = useAdminTab(fetcher, active)

  useEffect(() => {
    if (active) setPage(1)
  }, [debouncedSearch, active, setPage])

  const columns: Column<AdminWordSummary>[] = useMemo(
    () => [
      {
        key: 'lemma',
        label: 'Lemma',
        render: (w) => <span className="font-medium">{w.lemma}</span>,
      },
      {
        key: 'pos',
        label: 'POS',
        render: (w) => <span className="text-xs uppercase text-muted">{w.part_of_speech}</span>,
      },
      { key: 'language', label: 'Language', render: (w) => w.language_name },
      { key: 'senses', label: 'Senses', render: (w) => w.senses_count, className: 'text-right' },
      { key: 'forms', label: 'Forms', render: (w) => w.forms_count, className: 'text-right' },
      {
        key: 'trackers',
        label: 'Trackers',
        render: (w) => (
          <span className={w.trackers_count > 0 ? 'text-accent font-medium' : 'text-muted'}>
            {w.trackers_count}
          </span>
        ),
        className: 'text-right',
      },
      {
        key: 'actions',
        label: '',
        render: (w) => (
          <DeleteButton
            label={`Delete word ${w.lemma}`}
            onDelete={() => deleteAdminWord(w.id)}
            onSuccess={refresh}
          />
        ),
        className: 'w-10',
      },
    ],
    [refresh],
  )

  if (!active) return null

  return (
    <div>
      <div className="mb-4 flex items-center gap-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by lemma..."
          aria-label="Search words"
          className="w-full max-w-sm rounded-md border border-border-divider bg-transparent px-4 py-2 text-sm font-light text-body placeholder:text-muted focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30"
        />
        {loading && <LoadingBar />}
      </div>
      <AdminTable columns={columns} data={data} keyFn={(w) => w.id} />
      <Pagination meta={meta} onPageChange={setPage} />
    </div>
  )
}
